import {
	ClientError,
	GraphQLClient,
	RequestOptions,
	Variables,
} from "graphql-request";
import { createClient } from "graphql-ws";
import i18n from "i18next";
import localforage from "localforage";
import { DocumentNode, getOperationAST, print } from "graphql";
import { STORAGE_KEYS } from "./storage-keys";
import { createGraphQLErrorToast } from "./gql-error-toast";
import { toast } from "~served/ui-comps/toast";
import {
	getBusinessErrorDisplayMessage,
	isServedFaultError,
	parseGraphQLBusinessError,
} from "~served/configs/business-error";
import {
	browserWakeSignals,
	createResilientClient,
} from "~served/utils/realtime";
import { queryClient } from "~/providers/query-provider";
import { getState, setState } from "~/store";
import { RefreshTokenDocument } from "~/types/__generated/gql/graphql";

type RequestMiddleware = NonNullable<
	GraphQLClient["requestConfig"]["requestMiddleware"]
>;
type GraphQLMiddlewareResponse = Parameters<
	NonNullable<GraphQLClient["requestConfig"]["responseMiddleware"]>
>[0];

void (
	/* GraphQL */ `
		mutation RefreshToken($token: String!) {
			refreshToken(token: $token) {
				token
			}
		}
	`
);

const requestMiddleware: RequestMiddleware = async (request) => {
	const authHeaders = await getAuthHeaders();
	return { ...request, headers: { ...request.headers, ...authHeaders } };
};

const getReqId = (response: GraphQLMiddlewareResponse): string | undefined => {
	if (response instanceof ClientError) {
		// @ts-ignore
		return response.response.extensions?.reqId as string | undefined;
	}
	return undefined;
};

const responseMiddleware = (response: GraphQLMiddlewareResponse) => {
	// Suppress TOKEN_EXPIRED errors - they are handled silently by gqlReq
	if (response instanceof ClientError) {
		const isTokenExpired = response.response.errors?.some(
			(e) => e.extensions?.code === "TOKEN_EXPIRED",
		);
		if (isTokenExpired) return; // silent - gqlReq handles this
	}
	if (!(response instanceof ClientError)) return;

	const appError = parseGraphQLBusinessError(response.response.errors?.[0]);
	const message = appError
		? getBusinessErrorDisplayMessage(appError)
		: (response.response.errors?.[0]?.message ?? "Something went wrong!");
	const reqId = getReqId(response);
	const errorToast = createGraphQLErrorToast({
		message,
		code: appError?.code,
		isServedFault: isServedFaultError(appError),
		reference: reqId,
	});

	toast.error(errorToast.message, errorToast.options);
};

export const hubGqlClient = new GraphQLClient(process.env.HUB_GQL_URL!, {
	requestMiddleware,
	responseMiddleware,
});

const MAX_PAYMENT_REQUEST_BYTES = 1024 * 1024;

export const assertPaymentGraphQLRequestSize = (
	document: DocumentNode,
	variables: Variables,
) => {
	const operationName = getOperationAST(document)?.name?.value;
	const body = JSON.stringify({
		query: print(document),
		variables,
		operationName,
	});
	const bytes = new Blob([body]).size;
	if (bytes > MAX_PAYMENT_REQUEST_BYTES) {
		throw new Error(
			"This payment contains too many order items to submit at once. Clean up old orders or settle them separately.",
		);
	}
};

const getAuthHeaders = async () => {
	const obj = {} as Record<string, string>;

	const { venueId, token: _token, staffToken } = getState();
	const token =
		_token ?? (await localforage.getItem<string>(STORAGE_KEYS.TOKEN));

	if (token && obj.Authorization === undefined)
		obj.Authorization = `Bearer ${token}`;
	if (venueId) obj["x-served-venue-id"] = venueId;
	if (staffToken) obj["x-served-staff-token"] = staffToken;
	obj["x-served-staff-app-signature"] = new Date().getTime().toString();
	obj["x-served-laguage"] = i18n.language || "en";

	return obj;
};

const ACCESS_TOKEN_REFRESH_SKEW_MS = 60_000;

const isAccessTokenExpiring = (token: string) => {
	try {
		const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
		const { exp } = JSON.parse(atob(payload)) as { exp?: number };
		return !!exp && exp * 1000 - Date.now() < ACCESS_TOKEN_REFRESH_SKEW_MS;
	} catch {
		return false;
	}
};

/**
 * A reconnect after sleep usually happens with an expired access token, and
 * the Hub authorizes each subscription with the connection params. Refresh
 * first so re-subscriptions are not rejected; on failure fall back to the
 * current token and let `onAuthExpired` decide.
 */
const getWsConnectionParams = async () => {
	const token =
		getState().token ?? (await localforage.getItem<string>(STORAGE_KEYS.TOKEN));
	if (token && isAccessTokenExpiring(token))
		await refreshAccessToken().catch(() => undefined);
	return getAuthHeaders();
};

const isAuthRejection = (error: unknown) =>
	error instanceof ClientError ||
	(error instanceof Error && error.message === NO_REFRESH_TOKEN);

export const getHubGqlWsClient = () =>
	createResilientClient({
		createClient,
		url: process.env.HUB_GQL_WS_URL!,
		connectionParams: getWsConnectionParams,
		wakeSignals: browserWakeSignals,
		onConnectionChange: (connected) =>
			setState({ isConnectedToServer: connected }),
		// Subscription events published while disconnected are gone; refetch
		// active queries the same way a browser `online` event would.
		onReconnected: () => queryClient.getQueryCache().onOnline(),
		onAuthExpired: async () => {
			try {
				await refreshAccessToken();
				return true;
			} catch (error) {
				// Network failures are retried; a rejected session is not.
				if (!isAuthRejection(error)) return true;
				void getState().logout();
				return false;
			}
		},
	});

const NO_REFRESH_TOKEN = "No refresh token";

// Module-level mutex - prevents concurrent refresh storms
let refreshingPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
	if (refreshingPromise) return refreshingPromise;

	refreshingPromise = (async () => {
		const storedRefreshToken = await localforage.getItem<string>(
			STORAGE_KEYS.REFRESH_TOKEN,
		);
		if (!storedRefreshToken) {
			console.info("[auth] legacy session detected, forcing re-login");
			throw new Error(NO_REFRESH_TOKEN);
		}

		// Separate client without auth middleware to avoid infinite loop
		const client = new GraphQLClient(process.env.HUB_GQL_URL!);
		const {
			refreshToken: { token: newAccessToken },
		} = await client.request({
			document: RefreshTokenDocument,
			variables: { token: storedRefreshToken },
		});

		// Update state and storage
		setState({ token: newAccessToken });
		await localforage.setItem(STORAGE_KEYS.TOKEN, newAccessToken);

		// Notify other open tabs
		const channel = new BroadcastChannel("served-staff-auth");
		channel.postMessage({
			type: "TOKEN_REFRESHED",
			token: newAccessToken,
		});
		channel.close();

		return newAccessToken;
	})().finally(() => {
		refreshingPromise = null;
	});

	return refreshingPromise;
}

export const gqlReqAll = async <
	T extends Record<string, any>,
	V extends Variables = Variables,
>(
	opts: Omit<RequestOptions<V, T>, "signal"> & { signal?: AbortSignal },
	retried = false,
): Promise<T> => {
	try {
		return await hubGqlClient.request<T, V>(opts as RequestOptions<V, T>);
	} catch (err) {
		if (err instanceof ClientError && !retried) {
			const isExpired = err.response.errors?.some(
				(e) => e.extensions?.code === "TOKEN_EXPIRED",
			);
			if (isExpired) {
				try {
					await refreshAccessToken();
				} catch {
					void getState().logout();
					throw err;
				}
				// Retry with new token
				const authHeaders = await getAuthHeaders();
				return gqlReqAll(
					{
						...opts,
						requestHeaders: { ...opts.requestHeaders, ...authHeaders },
					},
					true,
				);
			}
		}
		throw err;
	}
};

export const gqlReq = async <
	T extends Record<string, any>,
	V extends Variables = Variables,
>(
	opts: Omit<RequestOptions<V, T>, "signal"> & { signal?: AbortSignal },
): Promise<T[keyof T]> => {
	const response = await gqlReqAll(opts);
	return response[Object.keys(response)[0]];
};

// BroadcastChannel listener for cross-tab token sync
if (typeof BroadcastChannel !== "undefined") {
	const channel = new BroadcastChannel("served-staff-auth");
	channel.onmessage = (event) => {
		if (event.data?.type === "TOKEN_REFRESHED") {
			setState({ token: event.data.token });
			void localforage.setItem(STORAGE_KEYS.TOKEN, event.data.token);
		}
	};
}
